import type { Bauvorhaben } from '@/types';

/**
 * Timeline-Helfer für Bauvorhaben: reines TS, keine React-Abhängigkeit.
 * Date-only-Mathe über Date.UTC aus den YYYY-MM-DD-Regex-Teilen — kein
 * new Date() auf die API-Strings, damit keine Zeitzone ins Spiel kommt
 * (gleiche Konvention wie das inline formatDate in den Listen).
 */

/** 'YYYY-MM-DD…' → UTC-Tageszahl (oder null bei fehlendem/ungültigem Format). */
function toUtcDay(dateStr?: string | null): number | null {
  if (!dateStr) return null;
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86400000;
}

/** Heute als UTC-Tageszahl aus den lokalen Datumsteilen. */
function todayUtcDay(): number {
  const now = new Date();
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / 86400000;
}

/** DATE-Spalte (YYYY-MM-DD) direkt als dd.MM.yyyy rendern. */
export function formatDateOnly(dateStr?: string | null): string {
  if (!dateStr) return '';
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return dateStr;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

export type TrackKind = 'done' | 'on' | 'warn' | 'late' | 'overdue' | 'future' | 'none' | 'cancelled';

export interface TrackInfo {
  kind: TrackKind;
  label: string;
  chipClasses: string;
}

const CHIP = {
  green: 'bg-green-100 text-green-800',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
  gray: 'bg-gray-100 text-gray-600',
} as const;

/**
 * On-Track-Einschätzung: tatsächlicher (gewichteter) Fortschritt vs.
 * verstrichene Zeit. Reihenfolge der Regeln ist wichtig.
 */
export function computeOnTrack(
  b: Pick<Bauvorhaben, 'status' | 'start_date' | 'end_date' | 'progress_percent'>
): TrackInfo {
  if (b.status === 'abgebrochen') {
    return { kind: 'cancelled', label: 'Abgebrochen', chipClasses: CHIP.gray };
  }
  if ((b.progress_percent ?? 0) >= 100) {
    return { kind: 'done', label: 'Fertig', chipClasses: CHIP.green };
  }
  const start = toUtcDay(b.start_date);
  const end = toUtcDay(b.end_date);
  const today = todayUtcDay();
  if (start === null || end === null) {
    return { kind: 'none', label: '', chipClasses: '' };
  }
  if (today < start) {
    return { kind: 'future', label: `Beginnt am ${formatDateOnly(b.start_date)}`, chipClasses: CHIP.gray };
  }
  if (today > end) {
    return { kind: 'overdue', label: 'Überfällig', chipClasses: CHIP.red };
  }
  const totalDays = end - start;
  if (totalDays <= 0) {
    return { kind: 'none', label: '', chipClasses: '' };
  }
  const expected = Math.min(1, Math.max(0, (today - start) / totalDays));
  const actual = (b.progress_percent ?? 0) / 100;
  const delta = actual - expected;
  if (delta >= -0.1) {
    return { kind: 'on', label: 'Im Plan', chipClasses: CHIP.green };
  }
  if (delta >= -0.25) {
    return { kind: 'warn', label: 'Leichte Verzögerung', chipClasses: CHIP.amber };
  }
  return { kind: 'late', label: 'Verzögert', chipClasses: CHIP.red };
}